/**
 * 后台 - 图片 Adapter 管理
 *
 * 列出所有已注册的图片 provider（adapter），展示能力矩阵，并允许管理员把
 * 暂不希望使用的 adapter 隐藏掉（不会影响已配置的渠道继续运行）。
 *
 * 数据源：GET /api/channels/providers/meta（含 isHidden 字段）
 * 操作：PATCH /api/channels/providers/:id/state
 */

import { useEffect, useMemo, useState } from 'react';
import { Eye, EyeOff, Info, Loader2, X } from 'lucide-react';
import { apiChannelAPI, type ImageProviderMetaInfo } from '../../services/api';
import toast from '../Toast';

export default function ImageProvidersTab() {
  const [providers, setProviders] = useState<ImageProviderMetaInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [detailProvider, setDetailProvider] = useState<ImageProviderMetaInfo | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiChannelAPI.getProviderMeta();
      setProviders(res.data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '加载 Adapter 列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const visibleCount = useMemo(
    () => providers.filter((p) => !p.isHidden).length,
    [providers]
  );

  const handleToggleHidden = async (provider: ImageProviderMetaInfo) => {
    const next = !provider.isHidden;
    setSavingId(provider.id);
    try {
      await apiChannelAPI.setProviderHidden(provider.id, next);
      setProviders((prev) =>
        prev.map((p) => (p.id === provider.id ? { ...p, isHidden: next } : p))
      );
      toast.success(next ? '已隐藏，不再出现在新建渠道选项中' : '已恢复显示');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '更新失败');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-light-text">图片 Adapter 管理</h2>
          <p className="text-sm text-light-muted mt-1">
            管理已注册的图片上游 adapter，可隐藏暂不希望使用的 adapter
          </p>
        </div>
        <div className="text-sm text-light-muted">
          共 {providers.length} 个，可见 {visibleCount} 个
        </div>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        隐藏只影响「新建/编辑渠道」时的 Provider 选项与本页徽章。已经配置过该 adapter 的 API 渠道
        会继续正常工作，不会被强制下线。
      </div>

      {loading ? (
        <div className="py-12 text-center text-light-muted">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          加载中
        </div>
      ) : providers.length === 0 ? (
        <div className="py-12 text-center text-light-muted text-sm">
          没有注册的图片 Adapter
        </div>
      ) : (
        <div className="bg-white border border-light-border rounded-2xl overflow-hidden">
          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full divide-y divide-light-border text-sm">
              <thead className="bg-light-hover">
                <tr className="text-left text-xs uppercase tracking-wider text-light-muted">
                  <th className="px-4 py-3">Adapter</th>
                  <th className="px-4 py-3">支持模式</th>
                  <th className="px-4 py-3">参考图上限</th>
                  <th className="px-4 py-3">输出上限</th>
                  <th className="px-4 py-3">状态</th>
                  <th className="px-4 py-3 text-right">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-light-border">
                {providers.map((p) => (
                  <tr
                    key={p.id}
                    className={`hover:bg-light-hover/50 ${p.isHidden ? 'opacity-60' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${p.badgeClassName}`}>
                          {p.badgeLabel}
                        </span>
                      </div>
                      <div className="text-xs font-mono text-light-muted mt-0.5">{p.id}</div>
                    </td>
                    <td className="px-4 py-3">
                      <ModeChips modeSupport={p.modeSupport} />
                    </td>
                    <td className="px-4 py-3 text-light-secondary">
                      {p.maxReferenceImageMaxCount != null
                        ? `${p.maxReferenceImageMaxCount} 张`
                        : '按渠道配置'}
                    </td>
                    <td className="px-4 py-3 text-light-secondary">
                      {p.maxOutputImageCount != null
                        ? `${p.maxOutputImageCount} 张`
                        : '不限'}
                    </td>
                    <td className="px-4 py-3">
                      <VisibilityPill isHidden={p.isHidden} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ProviderActions
                        provider={p}
                        savingId={savingId}
                        onOpenDetail={setDetailProvider}
                        onToggleHidden={handleToggleHidden}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="divide-y divide-light-border lg:hidden">
            {providers.map((p) => (
              <div key={p.id} className={`p-4 space-y-3 ${p.isHidden ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${p.badgeClassName}`}>
                      {p.badgeLabel}
                    </span>
                    <div className="mt-1 text-xs font-mono text-light-muted break-all">{p.id}</div>
                  </div>
                  <VisibilityPill isHidden={p.isHidden} />
                </div>
                <div>
                  <div className="mb-1 text-xs text-light-muted">支持模式</div>
                  <ModeChips modeSupport={p.modeSupport} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <InfoBlock
                    label="参考图上限"
                    value={
                      p.maxReferenceImageMaxCount != null
                        ? `${p.maxReferenceImageMaxCount} 张`
                        : '按渠道配置'
                    }
                  />
                  <InfoBlock
                    label="输出上限"
                    value={p.maxOutputImageCount != null ? `${p.maxOutputImageCount} 张` : '不限'}
                  />
                </div>
                <ProviderActions
                  provider={p}
                  savingId={savingId}
                  onOpenDetail={setDetailProvider}
                  onToggleHidden={handleToggleHidden}
                  mobile
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {detailProvider && (
        <ProviderDetailModal
          provider={detailProvider}
          onClose={() => setDetailProvider(null)}
        />
      )}
    </div>
  );
}

// ---------------- 子组件 ----------------

function ModeBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="px-1.5 py-0.5 rounded text-xs bg-light-hover text-light-secondary">
      {children}
    </span>
  );
}

function ModeChips({ modeSupport }: { modeSupport: ImageProviderMetaInfo['modeSupport'] }) {
  return (
    <div className="flex flex-wrap gap-1">
      {modeSupport.text2img && <ModeBadge>文生图</ModeBadge>}
      {modeSupport.img2img && <ModeBadge>图生图</ModeBadge>}
      {modeSupport.inpaint && <ModeBadge>局部重绘</ModeBadge>}
      {!modeSupport.text2img && !modeSupport.img2img && !modeSupport.inpaint && (
        <span className="text-xs text-light-muted">—</span>
      )}
    </div>
  );
}

function VisibilityPill({ isHidden }: { isHidden: boolean }) {
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
        isHidden ? 'bg-zinc-100 text-zinc-600' : 'bg-emerald-100 text-emerald-700'
      }`}
    >
      {isHidden ? '已隐藏' : '可见'}
    </span>
  );
}

function InfoBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-light-hover p-3">
      <div className="text-xs text-light-muted">{label}</div>
      <div className="mt-1 text-sm text-light-text">{value}</div>
    </div>
  );
}

function ProviderActions({
  provider,
  savingId,
  onOpenDetail,
  onToggleHidden,
  mobile = false,
}: {
  provider: ImageProviderMetaInfo;
  savingId: string | null;
  onOpenDetail: (provider: ImageProviderMetaInfo) => void;
  onToggleHidden: (provider: ImageProviderMetaInfo) => void;
  mobile?: boolean;
}) {
  return (
    <div className={mobile ? 'flex flex-wrap gap-2' : 'inline-flex items-center gap-1.5'}>
      <button
        onClick={() => onOpenDetail(provider)}
        className={mobile
          ? 'inline-flex items-center gap-1.5 rounded-lg border border-light-border px-3 py-2 text-xs text-light-secondary hover:bg-light-hover'
          : 'p-1.5 rounded-lg hover:bg-light-hover text-light-secondary'}
        title="查看能力详情"
      >
        <Info className="w-4 h-4" />
        {mobile && '详情'}
      </button>
      <button
        onClick={() => onToggleHidden(provider)}
        className={mobile
          ? 'inline-flex items-center gap-1.5 rounded-lg border border-light-border px-3 py-2 text-xs text-light-secondary hover:bg-light-hover'
          : `p-1.5 rounded-lg ${
              provider.isHidden
                ? 'hover:bg-emerald-50 text-emerald-600'
                : 'hover:bg-zinc-100 text-zinc-500'
            }`}
        title={provider.isHidden ? '恢复显示' : '隐藏 Adapter'}
        disabled={savingId === provider.id}
      >
        {savingId === provider.id ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : provider.isHidden ? (
          <Eye className="w-4 h-4" />
        ) : (
          <EyeOff className="w-4 h-4" />
        )}
        {mobile && (provider.isHidden ? '恢复显示' : '隐藏')}
      </button>
    </div>
  );
}

interface ProviderDetailModalProps {
  provider: ImageProviderMetaInfo;
  onClose: () => void;
}

function ProviderDetailModal({ provider, onClose }: ProviderDetailModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-light-border flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-light-text">{provider.label}</h3>
            <div className="text-xs font-mono text-light-muted mt-0.5">{provider.id}</div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-light-hover text-light-secondary"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-5 text-sm">
          <DetailSection title="能力概述">
            <p className="text-light-secondary leading-relaxed">{provider.adminCapability}</p>
          </DetailSection>

          <DetailSection title="详细说明">
            <p className="text-light-secondary leading-relaxed whitespace-pre-wrap">
              {provider.adminDetail}
            </p>
          </DetailSection>

          <DetailSection title="模式支持">
            <DetailRow label="文生图" value={booleanLabel(provider.modeSupport.text2img)} />
            <DetailRow label="图生图" value={booleanLabel(provider.modeSupport.img2img)} />
            <DetailRow label="局部重绘" value={booleanLabel(provider.modeSupport.inpaint)} />
          </DetailSection>

          <DetailSection title="参数边界">
            <DetailRow
              label="默认参考图上限"
              value={
                provider.defaultReferenceImageMaxCount != null
                  ? `${provider.defaultReferenceImageMaxCount} 张`
                  : '按渠道配置'
              }
            />
            <DetailRow
              label="最大参考图上限"
              value={
                provider.maxReferenceImageMaxCount != null
                  ? `${provider.maxReferenceImageMaxCount} 张`
                  : '不限'
              }
            />
            <DetailRow
              label="最大输出图数"
              value={
                provider.maxOutputImageCount != null
                  ? `${provider.maxOutputImageCount} 张`
                  : '不限'
              }
            />
          </DetailSection>
        </div>

        <div className="px-6 py-3 border-t border-light-border bg-light-hover/40 flex justify-end">
          <button onClick={onClose} className="btn-secondary">
            关闭
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-xs uppercase tracking-wider text-light-muted mb-2">{title}</h4>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-light-muted">{label}</span>
      <span className="text-light-text font-medium">{value}</span>
    </div>
  );
}

function booleanLabel(v: boolean): React.ReactNode {
  return v ? (
    <span className="text-emerald-600">支持</span>
  ) : (
    <span className="text-zinc-400">不支持</span>
  );
}
